import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../api/client'
import { useExamFramework } from '../../hooks/useExamFramework'
import AppLayout from '../../components/layout/AppLayout'

const H = ({ style, ...p }: React.HTMLAttributes<HTMLElement>) => (
  <span style={{ fontFamily: "'Space Grotesk', sans-serif", ...style }} {...p} />
)

const SKILL_META = {
  listening: { label: 'Nghe', icon: 'headphones', color: '#006571', bg: '#e6fafb', duration: '~40 phút', parts: '4 phần, ~40 câu' },
  reading:   { label: 'Đọc',  icon: 'menu_book',  color: '#004be2', bg: '#e8eeff', duration: '60 phút',  parts: '3 phần, ~40 câu' },
  writing:   { label: 'Viết', icon: 'edit_note',  color: '#8e00d5', bg: '#f5e6ff', duration: '60 phút',  parts: 'Task 1 + Task 2' },
  speaking:  { label: 'Nói',  icon: 'mic',        color: '#b41340', bg: '#ffe8ed', duration: '12–15 phút', parts: '3 phần phỏng vấn' },
} as const

type Skill = keyof typeof SKILL_META
type Phase = 'home' | 'test' | 'report'

export default function MockTestPage() {
  const { framework, displayName } = useExamFramework()
  // Build a parts summary per skill from the framework (FR-029); fall back to
  // the static SKILL_META string when the framework hasn't loaded.
  const partsSummary = (skill: Skill): string => {
    const ps = framework?.skills?.[skill]
    if (!ps || ps.length === 0) return SKILL_META[skill].parts
    const totalQ = ps.reduce((s, p) => s + (p.questionCount ?? 0), 0)
    const unit = skill === 'writing' ? 'tasks' : 'phần'
    return totalQ > 0 ? `${ps.length} ${unit}, ${totalQ} câu` : `${ps.length} ${unit}`
  }

  const [phase, setPhase] = useState<Phase>('home')
  const [testId, setTestId] = useState<string | null>(null)
  const [reportId, setReportId] = useState<string | null>(null)
  const qc = useQueryClient()

  const { data: history, isLoading: histLoading } = useQuery({
    queryKey: ['mock-test-history'],
    queryFn: () => api.get<any[]>('/mock-test/history'),
  })

  const startMutation = useMutation({
    mutationFn: () => api.post<any>('/mock-test/start'),
    onSuccess: (data) => {
      setTestId(data.testId)
      setPhase('test')
    },
  })

  if (phase === 'test' && testId) {
    return (
      <MockTestSession
        testId={testId}
        onComplete={(id) => { setReportId(id); setPhase('report'); qc.invalidateQueries({ queryKey: ['mock-test-history'] }) }}
        onExit={() => setPhase('home')}
      />
    )
  }

  if (phase === 'report' && reportId) {
    return <MockTestReport testId={reportId} onBack={() => setPhase('home')} />
  }

  return (
    <AppLayout>
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
          <span className="material-symbols-outlined" style={{ color: '#004be2', fontSize: '1.125rem', fontVariationSettings: "'FILL' 1" }}>quiz</span>
          <span style={{ fontSize: '0.6875rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#004be2' }}>Thi thử</span>
        </div>
        <H style={{ display: 'block', fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', fontWeight: 900, letterSpacing: '-0.03em', marginBottom: '0.5rem' }}>
          {displayName} <span style={{ color: '#004be2', fontStyle: 'italic' }}>Mock Test</span>
        </H>
        <p style={{ color: '#535b71', fontSize: '1rem', lineHeight: 1.6 }}>
          Mô phỏng đúng cấu trúc và thời gian đề thi {displayName} thật. 4 kỹ năng.
        </p>
      </div>

      {/* Parts grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(14rem, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
        {(Object.entries(SKILL_META) as [Skill, typeof SKILL_META[Skill]][]).map(([key, meta]) => (
          <div key={key} style={{ background: 'white', borderRadius: '1.5rem', padding: '1.75rem', boxShadow: '0 8px 24px rgba(0,0,0,0.04)', display: 'flex', alignItems: 'center', gap: '1.25rem' }}>
            <div style={{ width: '3.5rem', height: '3.5rem', borderRadius: '1rem', background: meta.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <span className="material-symbols-outlined" style={{ color: meta.color, fontSize: '1.75rem', fontVariationSettings: "'FILL' 1" }}>{meta.icon}</span>
            </div>
            <div>
              <H style={{ display: 'block', fontWeight: 800, fontSize: '1.125rem', marginBottom: '0.25rem' }}>{meta.label}</H>
              <span style={{ fontSize: '0.8125rem', color: '#535b71', display: 'block' }}>{partsSummary(key)}</span>
              <span style={{ fontSize: '0.75rem', fontWeight: 700, color: meta.color, marginTop: '0.25rem', display: 'block' }}>{meta.duration}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Start CTA */}
      <div style={{ background: 'linear-gradient(135deg, #060e20, #272e42)', borderRadius: '2rem', padding: '3rem', textAlign: 'center', position: 'relative', overflow: 'hidden', marginBottom: '2rem' }}>
        <div style={{ position: 'absolute', bottom: '-3rem', right: '-3rem', width: '14rem', height: '14rem', background: 'rgba(0,75,226,0.2)', borderRadius: '50%', filter: 'blur(40px)' }} />
        <div style={{ position: 'absolute', top: '-2rem', left: '-2rem', width: '10rem', height: '10rem', background: 'rgba(142,0,213,0.2)', borderRadius: '50%', filter: 'blur(40px)' }} />
        <H style={{ display: 'block', fontSize: '1.75rem', fontWeight: 900, color: 'white', marginBottom: '0.75rem', position: 'relative' }}>
          Sẵn sàng thi thử?
        </H>
        <p style={{ color: 'rgba(255,255,255,0.65)', lineHeight: 1.7, maxWidth: '30rem', margin: '0 auto 2rem', position: 'relative' }}>
          4 kỹ năng, đúng thời gian và cấu trúc đề thi thật. AI chấm điểm và phân tích sau khi thi.
        </p>
        <button
          onClick={() => startMutation.mutate()}
          disabled={startMutation.isPending}
          style={{
            padding: '1.125rem 3rem', borderRadius: '9999px', border: 'none',
            background: 'linear-gradient(135deg, #004be2, #809bff)', color: 'white',
            fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: '1rem',
            cursor: startMutation.isPending ? 'not-allowed' : 'pointer', position: 'relative',
            display: 'inline-flex', alignItems: 'center', gap: '0.75rem',
            boxShadow: '0 8px 24px rgba(0,75,226,0.4)',
          }}
        >
          {startMutation.isPending
            ? <><span className="spinner" style={{ width: '1.25rem', height: '1.25rem', borderColor: 'rgba(255,255,255,0.3)', borderTopColor: 'white' }} /> Đang tạo đề...</>
            : <><span className="material-symbols-outlined" style={{ fontSize: '1.375rem', fontVariationSettings: "'FILL' 1" }}>play_arrow</span> Bắt đầu thi thử</>
          }
        </button>
      </div>

      {/* History */}
      {!histLoading && history && history.length > 0 && (
        <div style={{ background: 'white', borderRadius: '1.5rem', padding: '2rem', boxShadow: '0 8px 24px rgba(0,0,0,0.04)' }}>
          <H style={{ display: 'block', fontWeight: 800, fontSize: '1.125rem', marginBottom: '1.25rem' }}>Lịch sử thi thử</H>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {history.map((test: any) => (
              <div
                key={test.id}
                onClick={() => { setReportId(test.id); setPhase('report') }}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem 1.25rem', background: '#f6f6ff', borderRadius: '1rem', cursor: 'pointer', transition: 'background 0.2s' }}
                onMouseEnter={e => { e.currentTarget.style.background = '#eef0ff' }}
                onMouseLeave={e => { e.currentTarget.style.background = '#f6f6ff' }}
              >
                <div>
                  <H style={{ display: 'block', fontWeight: 700, fontSize: '0.9375rem' }}>
                    {new Date(test.completedAt).toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                  </H>
                  <span style={{ fontSize: '0.8125rem', color: '#535b71' }}>
                    L: {test.scores.listening?.toFixed(1) ?? '—'} · R: {test.scores.reading?.toFixed(1) ?? '—'} · W: {test.scores.writing?.toFixed(1) ?? '—'} · S: {test.scores.speaking?.toFixed(1) ?? '—'}
                  </span>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <H style={{ display: 'block', fontWeight: 900, fontSize: '1.25rem', color: '#004be2' }}>{test.predictedLevel}</H>
                  <span style={{ fontSize: '0.75rem', color: '#535b71' }}>Overall: {test.overall?.toFixed(1)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </AppLayout>
  )
}

// ─── Mock Test Session ────────────────────────────────────────────────────────

const SKILL_ORDER: Skill[] = ['listening', 'reading', 'writing', 'speaking']

function MockTestSession({ testId, onComplete, onExit }: {
  testId: string
  onComplete: (id: string) => void
  onExit: () => void
}) {
  const [currentSkillIdx, setCurrentSkillIdx] = useState(0)
  const [answers, setAnswers] = useState<Record<string, string>>({})
  const [timeLeft, setTimeLeft] = useState<number | null>(null)
  const [confirming, setConfirming] = useState(false)

  const skill = SKILL_ORDER[currentSkillIdx]
  const meta = SKILL_META[skill]

  const { data: section, isLoading } = useQuery({
    queryKey: ['mock-test-section', testId, skill],
    queryFn: () => api.get<any>(`/mock-test/${testId}/questions?skill=${skill}`),
  })

  // Timer
  useState(() => {
    if (!section) return
    setTimeLeft(section.timeLimitMins * 60)
    const interval = setInterval(() => {
      setTimeLeft(t => {
        if (t === null || t <= 0) { clearInterval(interval); return 0 }
        return t - 1
      })
    }, 1000)
    return () => clearInterval(interval)
  })

  const answerMutation = useMutation({
    mutationFn: ({ questionId, answer }: { questionId: string; answer: string }) =>
      api.post<any>(`/mock-test/${testId}/answer`, { questionId, skill, answer, timeSpentSecs: 0 }),
  })

  const completeMutation = useMutation({
    mutationFn: () => api.post<any>(`/mock-test/${testId}/complete`),
    onSuccess: () => onComplete(testId),
  })

  async function handleNextSection() {
    // Submit all answers for current skill
    const questions = section?.questions ?? []
    for (const q of questions) {
      const ans = answers[q.id]
      if (ans) {
        await answerMutation.mutateAsync({ questionId: q.id, answer: ans })
      }
    }

    if (currentSkillIdx < SKILL_ORDER.length - 1) {
      setCurrentSkillIdx(i => i + 1)
      setAnswers({})
      setTimeLeft(null)
    } else {
      completeMutation.mutate()
    }
  }

  const formatTime = (secs: number) => {
    const m = Math.floor(secs / 60)
    const s = secs % 60
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
  }

  const questions = section?.questions ?? []
  const answeredCount = Object.keys(answers).length
  const isLastSkill = currentSkillIdx === SKILL_ORDER.length - 1

  return (
    <div style={{ minHeight: '100vh', background: '#f6f6ff', fontFamily: "'Manrope', sans-serif" }}>
      {/* Top bar */}
      <div style={{ background: 'white', borderBottom: '1px solid #eef0ff', padding: '1rem 2rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 100 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button onClick={onExit} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#535b71', display: 'flex', alignItems: 'center', gap: '0.375rem', fontSize: '0.875rem', fontWeight: 600 }}>
            <span className="material-symbols-outlined" style={{ fontSize: '1.125rem' }}>arrow_back</span> Thoát
          </button>
          <div style={{ width: '1px', height: '1.5rem', background: '#d9e2ff' }} />
          <H style={{ fontWeight: 800, fontSize: '1rem' }}>VSTEP Mock Test</H>
        </div>

        {/* Skill progress */}
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {SKILL_ORDER.map((s, i) => {
            const m = SKILL_META[s]
            const done = i < currentSkillIdx
            const active = i === currentSkillIdx
            return (
              <div key={s} style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', padding: '0.375rem 0.875rem', borderRadius: '9999px', background: done ? '#e6fafb' : active ? m.bg : '#f6f6ff', border: `2px solid ${active ? m.color : done ? '#006571' : '#d9e2ff'}` }}>
                <span className="material-symbols-outlined" style={{ fontSize: '0.875rem', color: done ? '#006571' : active ? m.color : '#a5adc6', fontVariationSettings: "'FILL' 1" }}>{done ? 'check_circle' : m.icon}</span>
                <span style={{ fontSize: '0.75rem', fontWeight: 700, color: done ? '#006571' : active ? m.color : '#a5adc6' }}>{m.label}</span>
              </div>
            )
          })}
        </div>

        {/* Timer */}
        {timeLeft !== null && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem', borderRadius: '9999px', background: timeLeft < 300 ? '#ffe8ed' : '#eef0ff' }}>
            <span className="material-symbols-outlined" style={{ fontSize: '1rem', color: timeLeft < 300 ? '#b41340' : '#004be2' }}>timer</span>
            <H style={{ fontWeight: 800, color: timeLeft < 300 ? '#b41340' : '#004be2', fontSize: '0.9375rem' }}>{formatTime(timeLeft)}</H>
          </div>
        )}
      </div>

      <div style={{ maxWidth: '52rem', margin: '0 auto', padding: '2rem 1.5rem' }}>
        {/* Section header */}
        <div style={{ background: `linear-gradient(135deg, ${meta.color}, ${meta.color}99)`, borderRadius: '1.5rem', padding: '1.5rem 2rem', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '1rem', color: 'white' }}>
          <span className="material-symbols-outlined" style={{ fontSize: '2rem', fontVariationSettings: "'FILL' 1" }}>{meta.icon}</span>
          <div>
            <H style={{ display: 'block', fontWeight: 900, fontSize: '1.25rem', color: 'white' }}>Phần {meta.label}</H>
            <span style={{ opacity: 0.8, fontSize: '0.875rem' }}>{meta.parts} · {meta.duration}</span>
          </div>
          <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
            <H style={{ display: 'block', fontWeight: 900, fontSize: '1.5rem', color: 'white' }}>{answeredCount}/{questions.length}</H>
            <span style={{ opacity: 0.8, fontSize: '0.75rem' }}>đã trả lời</span>
          </div>
        </div>

        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}><div className="spinner" /></div>
        ) : section?.isEmpty ? (
          <div style={{ background: 'white', borderRadius: '1.5rem', padding: '3rem', textAlign: 'center', boxShadow: '0 8px 24px rgba(0,0,0,0.04)' }}>
            <span className="material-symbols-outlined" style={{ fontSize: '3rem', color: '#a5adc6', display: 'block', marginBottom: '1rem' }}>inventory_2</span>
            <H style={{ display: 'block', fontWeight: 700, color: '#535b71', marginBottom: '0.5rem' }}>Chưa có câu hỏi cho phần này</H>
            <p style={{ color: '#a5adc6', fontSize: '0.875rem', marginBottom: '1.5rem' }}>Admin cần publish nội dung trước khi thi thử.</p>
            <button onClick={handleNextSection} style={{ padding: '0.875rem 2rem', borderRadius: '9999px', border: 'none', background: meta.bg, color: meta.color, fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, cursor: 'pointer' }}>
              {isLastSkill ? 'Nộp bài' : 'Bỏ qua → Phần tiếp theo'}
            </button>
          </div>
        ) : (
          <>
            {/* Questions */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', marginBottom: '2rem' }}>
              {questions.map((q: any, idx: number) => (
                <QuestionCard
                  key={q.id}
                  question={q}
                  index={idx}
                  answer={answers[q.id] ?? ''}
                  skill={skill}
                  onAnswer={(ans) => setAnswers(prev => ({ ...prev, [q.id]: ans }))}
                />
              ))}
            </div>

            {/* Next section button */}
            {!confirming ? (
              <button
                onClick={() => setConfirming(true)}
                className="btn-full-mobile"
                style={{
                  width: '100%', padding: '1.125rem', borderRadius: '9999px', border: 'none',
                  background: `linear-gradient(135deg, ${meta.color}, ${meta.color}99)`, color: 'white',
                  fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: '1rem',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem',
                  boxShadow: `0 8px 24px ${meta.color}40`,
                }}
              >
                {isLastSkill ? 'Nộp bài & xem kết quả' : `Hoàn thành phần ${meta.label} →`}
                <span className="material-symbols-outlined" style={{ fontSize: '1.375rem' }}>arrow_forward</span>
              </button>
            ) : (
              <div style={{ background: 'white', borderRadius: '1.5rem', padding: '1.5rem', boxShadow: '0 8px 24px rgba(0,0,0,0.08)', textAlign: 'center' }}>
                <H style={{ display: 'block', fontWeight: 800, marginBottom: '0.5rem' }}>
                  {isLastSkill ? 'Nộp bài?' : `Chuyển sang phần ${SKILL_META[SKILL_ORDER[currentSkillIdx + 1]]?.label}?`}
                </H>
                <p style={{ color: '#535b71', fontSize: '0.875rem', marginBottom: '1.25rem' }}>
                  Bạn đã trả lời {answeredCount}/{questions.length} câu. {answeredCount < questions.length && 'Câu chưa trả lời sẽ bị bỏ qua.'}
                </p>
                <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
                  <button onClick={() => setConfirming(false)} style={{ padding: '0.875rem 1.5rem', borderRadius: '9999px', border: '2px solid #d9e2ff', background: 'white', color: '#535b71', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, cursor: 'pointer' }}>
                    Quay lại
                  </button>
                  <button
                    onClick={handleNextSection}
                    disabled={completeMutation.isPending || answerMutation.isPending}
                    style={{ padding: '0.875rem 1.5rem', borderRadius: '9999px', border: 'none', background: `linear-gradient(135deg, ${meta.color}, ${meta.color}99)`, color: 'white', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                  >
                    {(completeMutation.isPending || answerMutation.isPending) && <span className="spinner" style={{ width: '1rem', height: '1rem', borderColor: 'rgba(255,255,255,0.3)', borderTopColor: 'white' }} />}
                    {isLastSkill ? 'Nộp bài' : 'Xác nhận'}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

function QuestionCard({ question, index, answer, skill, onAnswer }: {
  question: any; index: number; answer: string; skill: Skill; onAnswer: (a: string) => void
}) {
  const meta = SKILL_META[skill]
  const isWriting = skill === 'writing'
  const isSpeaking = skill === 'speaking'

  return (
    <div style={{ background: 'white', borderRadius: '1.5rem', padding: '2rem', boxShadow: '0 4px 16px rgba(0,0,0,0.04)', border: answer ? `2px solid ${meta.color}30` : '2px solid transparent' }}>
      {question.content && (
        <div style={{ background: '#f6f6ff', borderRadius: '1rem', padding: '1.25rem', marginBottom: '1.25rem', fontSize: '0.9375rem', lineHeight: 1.7, color: '#272e42' }}>
          {question.content.transcript ?? question.content.title}
        </div>
      )}
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.25rem' }}>
        <div style={{ width: '2rem', height: '2rem', borderRadius: '0.625rem', background: answer ? meta.bg : '#f6f6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, border: `2px solid ${answer ? meta.color : '#d9e2ff'}` }}>
          <H style={{ fontWeight: 800, fontSize: '0.8125rem', color: answer ? meta.color : '#a5adc6' }}>{index + 1}</H>
        </div>
        <p style={{ fontSize: '1rem', fontWeight: 600, lineHeight: 1.6, color: '#272e42', margin: 0 }}>{question.prompt}</p>
      </div>

      {isWriting || isSpeaking ? (
        <textarea
          value={answer}
          onChange={e => onAnswer(e.target.value)}
          placeholder={isWriting ? 'Viết bài của bạn tại đây...' : 'Ghi chú câu trả lời nói của bạn...'}
          rows={isWriting ? 8 : 4}
          style={{ width: '100%', padding: '1rem', borderRadius: '0.75rem', background: '#f6f6ff', border: `2px solid ${answer ? meta.color + '60' : '#d9e2ff'}`, outline: 'none', fontFamily: "'Manrope', sans-serif", fontSize: '0.9375rem', lineHeight: 1.6, resize: 'vertical', transition: 'border-color 0.2s', boxSizing: 'border-box' }}
          onFocus={e => { e.target.style.borderColor = meta.color }}
          onBlur={e => { e.target.style.borderColor = answer ? meta.color + '60' : '#d9e2ff' }}
        />
      ) : question.options ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
          {question.options.map((opt: string, i: number) => {
            const sel = answer === opt
            const labels = ['A', 'B', 'C', 'D']
            return (
              <label key={i} onClick={() => onAnswer(opt)} style={{ display: 'flex', alignItems: 'center', gap: '0.875rem', padding: '0.875rem 1.125rem', borderRadius: '0.875rem', cursor: 'pointer', background: sel ? meta.bg : '#f6f6ff', border: `2px solid ${sel ? meta.color : 'transparent'}`, transition: 'all 0.15s' }}>
                <div style={{ width: '1.75rem', height: '1.75rem', borderRadius: '50%', flexShrink: 0, background: sel ? meta.color : '#d9e2ff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: sel ? 'white' : '#535b71', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 800, fontSize: '0.75rem', transition: 'all 0.15s' }}>
                  {labels[i]}
                </div>
                <span style={{ fontSize: '0.9375rem', fontWeight: sel ? 700 : 500, color: sel ? '#272e42' : '#535b71' }}>{opt}</span>
              </label>
            )
          })}
        </div>
      ) : (
        <input
          type="text"
          value={answer}
          onChange={e => onAnswer(e.target.value)}
          placeholder="Nhập câu trả lời..."
          style={{ width: '100%', padding: '0.875rem 1rem', borderRadius: '0.75rem', background: '#f6f6ff', border: `2px solid ${answer ? meta.color + '60' : '#d9e2ff'}`, outline: 'none', fontFamily: "'Manrope', sans-serif", fontSize: '0.9375rem', boxSizing: 'border-box' }}
        />
      )}
    </div>
  )
}

// ─── Mock Test Report ─────────────────────────────────────────────────────────

function MockTestReport({ testId, onBack }: { testId: string; onBack: () => void }) {
  const { data: report, isLoading } = useQuery({
    queryKey: ['mock-test-report', testId],
    queryFn: () => api.get<any>(`/mock-test/${testId}/report`),
  })

  if (isLoading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f6f6ff' }}>
      <div className="spinner" />
    </div>
  )
  if (!report) return null

  const levelColors: Record<string, string> = { A1: '#a5adc6', A2: '#535b71', B1: '#004be2', B2: '#8e00d5', C1: '#006571' }
  const levelColor = levelColors[report.predictedLevel] ?? '#004be2'

  return (
    <div style={{ minHeight: '100vh', background: '#f6f6ff', fontFamily: "'Manrope', sans-serif", padding: '2rem 1.5rem' }}>
      <div style={{ maxWidth: '48rem', margin: '0 auto' }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#535b71', display: 'flex', alignItems: 'center', gap: '0.375rem', fontSize: '0.875rem', fontWeight: 600, marginBottom: '1.5rem' }}>
          <span className="material-symbols-outlined" style={{ fontSize: '1.125rem' }}>arrow_back</span> Quay lại
        </button>

        {/* Overall score */}
        <div style={{ background: `linear-gradient(135deg, ${levelColor}, ${levelColor}99)`, borderRadius: '2rem', padding: '2.5rem', color: 'white', textAlign: 'center', marginBottom: '1.5rem', boxShadow: `0 20px 48px ${levelColor}40` }}>
          <span style={{ fontSize: '0.75rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', opacity: 0.75, display: 'block', marginBottom: '0.75rem' }}>Kết quả thi thử VSTEP</span>
          <H style={{ display: 'block', fontSize: '5rem', fontWeight: 900, letterSpacing: '-0.04em', color: 'white', lineHeight: 1 }}>{report.predictedLevel}</H>
          <p style={{ opacity: 0.8, marginTop: '0.75rem', fontSize: '1.125rem' }}>Điểm tổng: <strong>{report.overall?.toFixed(1)}/10</strong></p>
          <p style={{ opacity: 0.6, fontSize: '0.875rem', marginTop: '0.25rem' }}>
            {new Date(report.completedAt).toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
          </p>
        </div>

        {/* Skill breakdown */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(14rem, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
          {(Object.entries(SKILL_META) as [Skill, typeof SKILL_META[Skill]][]).map(([key, meta]) => {
            const score = report.scores[key]
            const stats = report.skillStats?.[key]
            return (
              <div key={key} style={{ background: 'white', borderRadius: '1.5rem', padding: '1.5rem', boxShadow: '0 8px 24px rgba(0,0,0,0.04)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                  <div style={{ width: '2.5rem', height: '2.5rem', borderRadius: '0.75rem', background: meta.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span className="material-symbols-outlined" style={{ color: meta.color, fontSize: '1.25rem', fontVariationSettings: "'FILL' 1" }}>{meta.icon}</span>
                  </div>
                  <H style={{ fontWeight: 800, fontSize: '1rem' }}>{meta.label}</H>
                </div>
                <H style={{ display: 'block', fontSize: '2.5rem', fontWeight: 900, color: meta.color, lineHeight: 1, marginBottom: '0.5rem' }}>
                  {score != null ? score.toFixed(1) : '—'}
                </H>
                <div style={{ height: '6px', borderRadius: '9999px', background: '#d9e2ff', overflow: 'hidden', marginBottom: '0.5rem' }}>
                  <div style={{ height: '100%', width: `${Math.min(100, (score ?? 0) * 10)}%`, background: `linear-gradient(90deg, ${meta.color}, ${meta.color}99)`, borderRadius: '9999px' }} />
                </div>
                {stats && (
                  <span style={{ fontSize: '0.75rem', color: '#535b71' }}>
                    {stats.correct}/{stats.total} câu đúng ({stats.accuracy}%)
                  </span>
                )}
              </div>
            )
          })}
        </div>

        {/* Recommendations */}
        <div style={{ background: '#eef0ff', borderRadius: '1.5rem', padding: '1.5rem' }}>
          <H style={{ display: 'block', fontWeight: 800, fontSize: '1rem', color: '#004be2', marginBottom: '1rem' }}>Gợi ý cải thiện</H>
          {Object.entries(report.scores as Record<string, number>)
            .sort(([, a], [, b]) => (a ?? 10) - (b ?? 10))
            .slice(0, 2)
            .map(([skill, score]) => {
              const meta = SKILL_META[skill as Skill]
              if (!meta) return null
              return (
                <div key={skill} style={{ display: 'flex', gap: '0.75rem', marginBottom: '0.75rem' }}>
                  <span className="material-symbols-outlined" style={{ color: meta.color, fontSize: '1.25rem', fontVariationSettings: "'FILL' 1", flexShrink: 0 }}>{meta.icon}</span>
                  <span style={{ fontSize: '0.875rem', color: '#272e42', lineHeight: 1.5 }}>
                    <strong>{meta.label}</strong> ({score?.toFixed(1) ?? '—'}/10) — Cần luyện tập thêm để cải thiện điểm số.
                  </span>
                </div>
              )
            })}
        </div>
      </div>
    </div>
  )
}
